const MFA_BYPASS_ALLOWLIST_SEPARATOR = /[\s,;]+/;

function normalizeEntry(value: string): string {
  return value.trim().toLowerCase();
}

function matchesWildcardPattern(value: string, pattern: string): boolean {
  const parts = pattern.split('*');
  let searchIndex = 0;

  if (!pattern.startsWith('*')) {
    const prefix = parts.shift() ?? '';
    if (!value.startsWith(prefix)) {
      return false;
    }
    searchIndex = prefix.length;
  }

  const suffix = pattern.endsWith('*') ? null : (parts.pop() ?? '');

  for (const part of parts) {
    if (!part) {
      continue;
    }

    const nextIndex = value.indexOf(part, searchIndex);
    if (nextIndex === -1) {
      return false;
    }
    searchIndex = nextIndex + part.length;
  }

  if (suffix === null) {
    return true;
  }

  return value.slice(searchIndex).endsWith(suffix);
}

export function parseMfaBypassAllowlist(rawValue: string | null | undefined): string[] {
  if (!rawValue) {
    return [];
  }

  return rawValue
    .split(MFA_BYPASS_ALLOWLIST_SEPARATOR)
    .map(normalizeEntry)
    .filter(Boolean);
}

export function emailMatchesMfaBypassAllowlist(
  email: string | null | undefined,
  rawAllowlist: string | null | undefined,
): boolean {
  const normalizedEmail = normalizeEntry(email ?? '');
  if (!normalizedEmail) {
    return false;
  }

  return parseMfaBypassAllowlist(rawAllowlist).some((pattern) => {
    if (!pattern.includes('*')) {
      return normalizedEmail === pattern;
    }

    return matchesWildcardPattern(normalizedEmail, pattern);
  });
}

// Hostnames that always run as production. A frontend bundle served from any
// of these must enforce MFA regardless of the build-time VITE_SENTRY_ENVIRONMENT
// value — that env var is baked into the artifact and would survive a
// preview/staging build being mis-promoted to production. Hostname is read
// from `window.location.hostname` at sign-in time, so it cannot be inherited
// from a misbuilt artifact.
const PRODUCTION_HOSTNAMES = new Set<string>([
  'app.supply-guard.io',
]);

function normalizeHostname(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

export function isProductionHostname(hostname: string | null | undefined): boolean {
  const normalized = normalizeHostname(hostname);
  if (!normalized) {
    return false;
  }
  return PRODUCTION_HOSTNAMES.has(normalized);
}

export function isProductionRuntime(
  runtimeEnvironment: string | null | undefined,
  runtimeHostname?: string | null | undefined,
): boolean {
  if (runtimeEnvironment && runtimeEnvironment.trim().toLowerCase() === 'production') {
    return true;
  }
  // True-runtime fallback: even if the build-time env says non-prod, refuse to
  // honour the allowlist when the page is served from a known production
  // hostname. Defends against a misbuilt artifact reaching production.
  if (isProductionHostname(runtimeHostname)) {
    return true;
  }
  return false;
}

// ENT-1418: in preview, MFA is fully off for every user — both forced
// enrollment and sign-in step-up. Preview is ephemeral, Supabase + Vercel
// branches are throwaway, and there is no security boundary to defend.
// Staging and production are unaffected and continue to enforce MFA per
// the existing allowlist + hostname rules.
//
// The bypass is strictly gated on:
//   - `runtimeEnvironment === 'preview'` (case-insensitive, trimmed)
//   - the page is NOT served from a known production hostname
//
// The production-hostname guard defends against a misbuilt artifact baking
// `VITE_SENTRY_ENVIRONMENT='preview'` into a bundle that is then promoted
// to `app.supply-guard.io`. Hostname is a true runtime signal that cannot
// be inherited from a misbuilt artifact, so it overrides the build-time
// env even when the env says 'preview'.
// ENT-1418 codex rounds 2+3: do NOT trust the raw `VITE_SENTRY_ENVIRONMENT`
// alone, but also do NOT treat every `*.vercel.app` host as preview for auth.
//
// The repo's real Enterprise preview URLs are the ephemeral
// `enterprise-staging-<hash>-supply247.vercel.app` deployments, while
// `enterprise-staging.vercel.app` is the canonical long-lived staging host.
// Gate the MFA bypass on that narrow preview-host pattern so a generic or
// production-facing Vercel URL cannot silently disable MFA.
const STAGING_HOSTNAMES = new Set<string>([
  'enterprise-staging.vercel.app',
  'staging.supply-guard.io',
]);

const ENTERPRISE_PREVIEW_VERCEL_HOSTNAME_PATTERN = /^enterprise-staging-[a-z0-9-]+\.vercel\.app$/;

// ENT-1418 follow-up: local dev mirrors preview — MFA is bypassed by
// default for every user, and `?mfa=on` forces the real flow back on. The
// only difference between local and preview is where the 2FA code comes
// from: local reads `public/.botbuddy-totp.json` (file-based), preview
// reads from BotBuddy server (CI-registered via register_totp_seed).
// Keeping the two environments symmetric removes a behavioural drift that
// otherwise made the same widget feel different between local and preview.
const LOCAL_HOSTNAMES = new Set<string>([
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '::1',
  // Browsers report `window.location.hostname` for IPv6 loopback URLs
  // (e.g. `http://[::1]:4179`) as the bracketed form `[::1]`. Without
  // this entry, IPv6-loopback sessions would fall through to non-local
  // behavior and unexpectedly see MFA prompts.
  '[::1]',
]);

function isLocalHostname(value: string | null | undefined): boolean {
  return LOCAL_HOSTNAMES.has(normalizeHostname(value));
}

function isStagingHostname(value: string | null | undefined): boolean {
  return STAGING_HOSTNAMES.has(normalizeHostname(value));
}

/**
 * ENT-4192: true when the app is running as the **staging** deployment. Gates
 * staging-only admin controls — the Marketplace Listings "Auto Scanning" (scan-cron
 * opt-in) column, which only affects staging's third-party API budget and is
 * meaningless in production/preview/local.
 *
 * Keys on the per-target build var `VITE_AMPLITUDE_ENVIRONMENT`, NOT
 * `VITE_SENTRY_ENVIRONMENT` and NOT the hostname (ENT-4192 Codex P2 ×2):
 *   - `VITE_SENTRY_ENVIRONMENT` is `staging` for BOTH preview and staging builds
 *     (shared Sentry config), so it cannot distinguish them.
 *   - the ephemeral `enterprise-staging-<hash>-supply247.vercel.app` host is used by
 *     BOTH preview and the staging build-verification deploy (`staging.yml` +
 *     `e2e/lib/sso-staging-urls.test.ts`), so hostname cannot distinguish them either.
 *   - `VITE_AMPLITUDE_ENVIRONMENT` IS set per target: `development` (local, `.env*`),
 *     `preview` (`preview.yml`), `staging` (`staging.yml`), `production`
 *     (`reusable-live-release-deploy.yml`).
 *
 * A known production hostname stays authoritative (returns false) to defend against a
 * staging artifact mis-promoted to production surfacing staging-only controls.
 */
export function isStagingRuntime(
  deploymentEnvironment: string | null | undefined,
  runtimeHostname?: string | null | undefined,
): boolean {
  if (isProductionHostname(runtimeHostname)) {
    return false;
  }
  return (deploymentEnvironment ?? '').trim().toLowerCase() === 'staging';
}

function isPreviewVercelHostname(value: string | null | undefined): boolean {
  const normalized = normalizeHostname(value);
  if (!normalized) {
    return false;
  }
  if (isStagingHostname(normalized) || isProductionHostname(normalized)) {
    return false;
  }
  return ENTERPRISE_PREVIEW_VERCEL_HOSTNAME_PATTERN.test(normalized);
}

// ENT-1418 opt-out: testers can force the real MFA flow back on in preview
// by passing `override === 'on'`. The wrapper in `@/lib/auth` resolves the
// override from `?mfa=on` URL param (persisted to sessionStorage). Any
// other value (undefined, 'off', '') leaves the preview bypass in place.
export function isPreviewMfaBypass(
  runtimeEnvironment?: string | null | undefined,
  runtimeHostname?: string | null | undefined,
  override?: string | null | undefined,
): boolean {
  const normalizedEnv = (runtimeEnvironment ?? '').trim().toLowerCase();

  if ((override ?? '').trim().toLowerCase() === 'on') {
    return false;
  }
  // Production + staging hostnames always force MFA on, regardless of what
  // the build-time env var says. Hostname is a true runtime signal that
  // cannot be inherited from a misbuilt or misconfigured bundle.
  if (isProductionHostname(runtimeHostname)) {
    return false;
  }
  if (isStagingHostname(runtimeHostname)) {
    return false;
  }
  // Staging deploys can be served from commit-scoped Vercel hostnames while
  // still carrying the staging runtime environment. Keep staging production-
  // parity MFA semantics in that case instead of treating the hostname shape
  // alone as a preview bypass signal.
  if (normalizedEnv === 'staging') {
    return false;
  }
  // Preview signal: either the runtime hostname matches the known ephemeral
  // Enterprise preview hostname pattern OR the build-time env explicitly says
  // preview. The first half catches mis-set / missing VITE_SENTRY_ENVIRONMENT
  // on a real preview deploy; the second half catches future preview hosts
  // that do not use the current Vercel naming pattern.
  if (isPreviewVercelHostname(runtimeHostname)) {
    return true;
  }
  // Local mirrors preview by design (see LOCAL_HOSTNAMES comment above).
  // Default-off MFA, `?mfa=on` opts back in.
  if (isLocalHostname(runtimeHostname)) {
    return true;
  }
  return normalizedEnv === 'preview';
}

export function isMfaRequiredForEmail(
  email: string | null | undefined,
  rawAllowlist: string | null | undefined,
  runtimeEnvironment?: string | null | undefined,
  runtimeHostname?: string | null | undefined,
  override?: string | null | undefined,
): boolean {
  if (isPreviewMfaBypass(runtimeEnvironment, runtimeHostname, override)) {
    return false;
  }
  if (isProductionRuntime(runtimeEnvironment, runtimeHostname)) {
    return true;
  }
  return !emailMatchesMfaBypassAllowlist(email, rawAllowlist);
}
